import { useEffect, useState } from 'react'
import { AddIcon, Box, Grid, IconButton, MinusIcon, styled, TextField } from 'opensolar-ui'

const QuantityButtonContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  height: '36px',
  borderRadius: '6px',
  color: 'white',
  overflow: 'hidden',
}))

const QuantityBox = styled(Grid)(() => ({
  borderTop: `1px solid #CDCDCD`,
  borderBottom: `1px solid #CDCDCD`,
  height: '36px',
  '& p': {
    textAlign: 'center',
    fontSize: '18px',
  },
}))

const StyledIconButton = styled(IconButton)(() => ({
  background: '#4272DD',
  color: 'white',
  padding: 0,
  height: '36px',
  width: '36px',
  borderRadius: 0,
  '&:hover': {
    background: '#113B98',
  },
}))

const StyledTextField = styled(TextField)(() => ({
  width: '100%',
  textAlign: 'center',
  border: 'none',
  background: 'transparent',
  color: 'white',
  padding: '0',

  '& .OSUI-OutlinedInput-notchedOutlinenotchedOutline': {
    border: 'none',
  },
  '&:hover .OSUI-OutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .Mui-focused .OSUI-OutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .OSUI-InputBase-root': {
    background: 'transparent',
  },
  '& fieldset': {
    border: 'none', // Remove the default border
  },
  '& .MuiInput-root': {
    fontSize: '18px',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before ': {
    borderBottom: 'none',
  },
}))

export const MAX_QUANTITY = 10000

const QuantityButton = ({
  onChangeQuantity,
  initialValue,
  disabled,
}: {
  onChangeQuantity: (quantity: number) => void
  initialValue?: number
  disabled?: boolean
}) => {
  const [displayValue, setDisplayValue] = useState(initialValue ?? 0)

  useEffect(() => {
    setDisplayValue(initialValue ?? 0)
  }, [initialValue])

  const handleQuantityChange = (event, delta: number) => {
    if (event.stopPropagation) event.stopPropagation()

    const newQuantity = Math.max(0, Math.min(MAX_QUANTITY, displayValue + delta))
    setDisplayValue(newQuantity)
    onChangeQuantity(newQuantity)
  }

  const handleInputChange = (event) => {
    if (event.stopPropagation) event.stopPropagation()

    const value = event.target.value

    if (value === '') {
      setDisplayValue(0)
      onChangeQuantity(0)
      return
    }

    if (/^[0-9]+$/.test(value)) {
      const num = Math.min(MAX_QUANTITY, Number(value))
      setDisplayValue(num)
      onChangeQuantity(num)
    }
  }

  const handleBlur = (event) => {
    if (event.stopPropagation) event.stopPropagation()

    const normalized = Math.max(0, Math.min(MAX_QUANTITY, displayValue))
    setDisplayValue(normalized)
    onChangeQuantity(normalized)
  }

  return (
    <QuantityButtonContainer>
      <Grid container direction="row" justify="space-between" alignItems="center" wrap="nowrap">
        <Grid item>
          <StyledIconButton onClick={(e) => handleQuantityChange(e, -1)} disabled={disabled}>
            <MinusIcon />
          </StyledIconButton>
        </Grid>
        <QuantityBox container item justify="center" alignContent="center" xs={11}>
          <StyledTextField
            onClick={(e) => e.stopPropagation()}
            value={displayValue}
            onChange={handleInputChange}
            variant="outlined"
            onBlur={handleBlur}
            inputProps={{
              min: 0,
              border: 'none',
              style: { textAlign: 'center' },
            }}
            disabled={disabled}
          />
        </QuantityBox>
        <Grid item>
          <StyledIconButton onClick={(e) => handleQuantityChange(e, 1)} disabled={disabled}>
            <AddIcon />
          </StyledIconButton>
        </Grid>
      </Grid>
    </QuantityButtonContainer>
  )
}

export default QuantityButton
